// @flow

import React, { useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";

import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { formatDate } from "@transformers/date";
import type { State } from "@store/reducers";
import type { PaginationType } from "@types";
import config from "@config";
import {
  Button,
  GSGrid,
  Icons,
  Pagination,
  View,
  Text,
  EmployeeSmsAddModal,
  EmployeeSmsEditModal,
  EmployeeSmsDeleteModal,
} from "@components";
import type {
  EmployeeSmsSettings,
  EmployeeSmsRule,
} from "@modules/service/reducer/employee-sms-notifications/types";
import serviceActionCreators from "@modules/service/actionCreator";

type PropsFromState = $Exact<{
  pagination: PaginationType,
  totalElements: number,
  list: Array<EmployeeSmsRule>,
  employeeSmsCurrent: ?EmployeeSmsRule,
  showAddEmployeeSmsRuleModal: boolean,
  showEditEmployeeSmsRuleModal: boolean,
  showDeleteEmployeeSmsRuleModal: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getRules: typeof actionCreators.GetEmployeeSmsRules,
  deleteEmployeeSmsRule: typeof actionCreators.DeleteEmployeeSmsRule,
  getEmployeeSmsSchema: typeof actionCreators.GetEmployeeSmsSchema,
  setEmployeeSmsCurrent: typeof actionCreators.SetEmployeeSmsCurrent,
  setEmployeeSmsAssignments: typeof actionCreators.SetEmployeeSmsAssignments,
  getEmployeeSmsAssignments: typeof actionCreators.GetEmployeeSmsAssignments,
  setShowAddEmployeeSmsModal: typeof actionCreators.SetShowAddEmployeeSmsModal,
  setShowEditEmployeeSmsModal: typeof actionCreators.SetShowEditEmployeeSmsModal,
  setShowDeleteEmployeeSmsModal: typeof actionCreators.SetShowDeleteEmployeeSmsModal,
}>;

type Props = $Exact<{
  sortChange: () => void,
  clientId: number,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export function HealthPromotions(props: Props) {
  useEffect(() => {
    // Employee SMS Rules
    props.getRules({ clientId: props.clientId });
    props.getEmployeeSmsSchema({ clientId: props.clientId });
  }, [props.clientId]);

  const columns = [
    {
      name: props.t("ID"),
      key: "id",
      sortable: true,
    },
    {
      name: props.t("EMPLOYEE_SMS_WELLBEING_ABSENCE_REASON"),
      key: "absenceReason",
      align: "left",
      renderCell: ({ row }: { row: EmployeeSmsRule }) => row.absenceReason.label,
      minWidth: 150,
    },
    {
      name: props.t("EMPLOYEE_SMS_WELLBEING_TEXT_MESSAGE"),
      key: "textMessage",
      align: "left",
      sortable: true,
      width: 740,
      renderCell: ({ row }: { row: EmployeeSmsRule }) => GSGrid.render.ellipsis(row.textMessage),
    },
    {
      name: props.t("EMPLOYEE_SMS_WELLBEING_DATE_ADDED"),
      key: "createdAt",
      sortable: true,
      renderCell: ({ row }: { row: EmployeeSmsRule }) => formatDate(row.createdAt),
    },
    {
      key: "status",
      name: props.t("EMPLOYEE_SMS_WELLBEING_STATUS"),
      align: "center",
      width: 100,
      frozen: "right",
      renderCell: ({ row }: { row: EmployeeSmsRule }) =>
        GSGrid.render.statusPill(props.t(row.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: props.t("EDIT"),
      key: "edit",
      align: "center",
      width: 100,
      frozen: "right",
      onCellClick: (row: EmployeeSmsRule) => showSMSEditModal(row),
      renderCell: ({ row }: { row: EmployeeSmsRule }) => GSGrid.render.materialIcon("edit"),
    },
    {
      name: props.t("DELETE"),
      key: "delete",
      align: "center",
      width: 100,
      frozen: "right",
      onCellClick: (row: EmployeeSmsRule) => showSMSDeleteModal(row),
      renderCell: ({ row }: { row: EmployeeSmsRule }) => GSGrid.render.materialIcon("delete"),
    },
  ];

  function getEmployeeSMSSort() {
    const orders = ((props.pagination || {}).sort || {}).orders || [];
    if (orders.length < 1) {
      return { id: null, direction: "desc" };
    }
    return { id: orders[0].property, direction: orders[0].direction.toLowerCase() };
  }

  function totalPages() {
    const totalElements = props.totalElements || 0;
    const totalPages = totalElements ? Math.ceil(totalElements / config.resultsPerPage) : 0;
    return totalPages;
  }

  function handlePaginationChange(page: number) {
    props.getRules({
      clientId: props.clientId,
      page,
    });
  }

  function showSMSAddModal() {
    props.setEmployeeSmsCurrent({});
    props.setEmployeeSmsAssignments([]);
    props.setShowAddEmployeeSmsModal(true);
  }

  function showSMSEditModal(item: EmployeeSmsRule) {
    props.setEmployeeSmsCurrent(item);
    props.getEmployeeSmsAssignments({ clientId: props.clientId, ruleId: item.id });
    props.setShowEditEmployeeSmsModal(true);
  }

  function showSMSDeleteModal(item: EmployeeSmsRule) {
    props.setEmployeeSmsCurrent(item);
    props.setShowDeleteEmployeeSmsModal(true);
  }

  function closeSMSAddModal(closeAction: Function) {
    return function() {
      closeAction(false);
    };
  }

  function handledeleteEmployeeSmsRule(ruleId: number) {
    props.deleteEmployeeSmsRule({ clientId: props.clientId, ruleId });
  }

  function sortEmployeeSMSChange(props: *) {
    props.getRules({
      clientId: props.clientId,
      sort: `${props.id},${props.direction}`,
    });
  }

  return (
    <View gap={4}>
      <Text className="message-box" text={props.t("FEATURES_MESSAGES_INFO")} />
      <View className="gs-container" gap={4}>
        <View direction="row" justifyContent="end">
          <Button materialIcon="add_box" onClick={showSMSAddModal}>
            {props.t("ADD")}
          </Button>
        </View>
        <View>
          {props.list && (
            <GSGrid
              onSortChange={sortEmployeeSMSChange}
              sort={getEmployeeSMSSort}
              rows={props.list}
              columns={columns}
              maxHeight={"100%"}
            />
          )}
        </View>
        {totalPages() > 1 && (
          <Pagination
            className="d-flex flex-fill justify-content-center"
            wrapperClassName=" m-0"
            currentPage={props.pagination.pageNumber}
            totalPages={totalPages()}
            onChange={handlePaginationChange}
          />
        )}
        {props.showAddEmployeeSmsRuleModal && <EmployeeSmsAddModal clientId={props.clientId} />}
        {props.showEditEmployeeSmsRuleModal && <EmployeeSmsEditModal clientId={props.clientId} />}
        {props.showDeleteEmployeeSmsRuleModal && (
          <EmployeeSmsDeleteModal
            id={(props.employeeSmsCurrent || {}).id}
            onCancel={closeSMSAddModal(props.setShowDeleteEmployeeSmsModal)}
            onSubmit={handledeleteEmployeeSmsRule}
          />
        )}
      </View>
    </View>
  );
}

const mapStateToProps = (state: State, props: Props): PropsFromState => ({
  pagination: serviceSelectors.getEmployeeSmsRulesPagination(state),
  totalElements: serviceSelectors.getEmployeeSmsRulesTotalElements(state),
  list: serviceSelectors.getEmployeeSmsRulesList(state),

  employeeSmsCurrent: serviceSelectors.getEmployeeSmsCurrent(state),
  showAddEmployeeSmsRuleModal: serviceSelectors.getShowAddEmployeeSmsRuleModal(state),
  showEditEmployeeSmsRuleModal: serviceSelectors.getShowEditEmployeeSmsRuleModal(state),
  showDeleteEmployeeSmsRuleModal: serviceSelectors.getShowDeleteEmployeeSmsRuleModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getRules: actionCreators.GetEmployeeSmsRules,
  deleteEmployeeSmsRule: actionCreators.DeleteEmployeeSmsRule,
  getEmployeeSmsSchema: actionCreators.GetEmployeeSmsSchema,
  setEmployeeSmsCurrent: actionCreators.SetEmployeeSmsCurrent,
  setEmployeeSmsAssignments: actionCreators.SetEmployeeSmsAssignments,
  getEmployeeSmsAssignments: actionCreators.GetEmployeeSmsAssignments,
  setShowAddEmployeeSmsModal: actionCreators.SetShowAddEmployeeSmsModal,
  setShowEditEmployeeSmsModal: actionCreators.SetShowEditEmployeeSmsModal,
  setShowDeleteEmployeeSmsModal: actionCreators.SetShowDeleteEmployeeSmsModal,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(HealthPromotions);
